import { AgGridReact } from "ag-grid-react";
import { AxiosResponse } from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import useFetch from "../../../hooks/useFetchMSAL";
import { LotSizeOptimizationPolicyFetchAPI } from "../../../models/APIResponses.model";
import LotSizeOptimizationPolicy from "../../../models/PlanningParameters/LotSizeOptimizationPolicy.model";
import { AG_GRID_DEFAULT_COL_DEF, AG_GRID_MODULES, AG_GRID_OPTIONS, LOT_SIZE_GROUPS_TAB_ID, LOT_SIZE_OPTIMIZATION_POLICY_TAB_ID, TOAST_CONTAINER_ID } from "../../../shared/constants";
import { getToastOptions, newGridRowStyleHandler, removeInsertedRowsById } from "../../../shared/functions";
import { RootState } from "../../../store";
import { planningParametersTabActions } from "../../../store/slices/planning-parameters-tab-slice";
import EditableGridActions from "../../UI/EditableGridActions/EditableGridActions";

const LotSizeOptimizationPolicyGrid = (props: {
    gridHeight: string;
}) => {
    // States
    const [gridRowData, setGridRowData] = useState<LotSizeOptimizationPolicy[]>([]);
    const [gridRowDataCopy, setGridRowDataCopy] = useState<LotSizeOptimizationPolicy[]>([]);
    const [partCodes, setPartCodes] = useState<string[]>([]);
    const [orderPolicies, setOrderPolicies] = useState<string[]>([]);
    const [safetyStockPolicies, setSafetyStockPolicies] = useState<string[]>([]);
    const [partCodeMappings, setPartCodeMappings] = useState<{ [key: string]: string }>({});
    const [changeTrackState, setChangeTrackState] = useState<boolean>(false);
    const [insertedRows, setInsertedRows] = useState<any>({});
    const [selectedRowIds, setSelectedRowIds] = useState<any[]>([]);
    const [newRowCount, setNewRowCount] = useState<number>(1);
    const [enableDelButton, setEnableDelButton] = useState(false);
    const [cellEditTracks, setCellEditTracks] = useState<{
        [key: number]: {
            [key: string]: any;
        }
    }>({});

    // Variables
    const [, fetchData] = useFetch([]);
    const agGridRef = useRef<AgGridReact>(null);
    const gridColDef: any[] = [
        {
            headerName: "Group Policy ID",
            field: "group_policy_id",
            cellDataType: "number",
            hide: true
        },
        {
            headerName: "Group Policy Name",
            field: "group_policy_name",
            cellDataType: "text",
            headerCheckboxSelection: true,
            checkboxSelection: true,
            showDisabledCheckboxes: true,
        },
        {
            headerName: "Part Name",
            field: "part_name",
            cellDataType: "text",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
                values: partCodes
            },
        },
        {
            headerName: "Order Policy",
            field: "order_policy",
            cellDataType: "text",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
                values: orderPolicies
            },
        },
        {
            headerName: "Safety Stock Policy",
            field: "safety_stock_policy",
            cellDataType: "text",
            cellEditor: "agSelectCellEditor",
            cellEditorParams: {
                values: safetyStockPolicies
            },
        }
    ];

    // Redux variables
    const dispatchFn = useDispatch();
    const selectedPartSlicers = useSelector((state: RootState) => state.planningParametersTab.selectedGridSlicers.part_name);
    const activeUpperSubTabId: string = useSelector((state: RootState) => state.planningParametersTab.activeSubTabId.upper);
    const tabApiDataFetched: boolean = useSelector((state: RootState) => state.planningParametersTab.tabApiDataFetched[LOT_SIZE_OPTIMIZATION_POLICY_TAB_ID]);
    const isLotSizeOptimizationPolicyUpdated = useSelector((state: RootState) => state.planningParametersTab.isLotSizeOptimizationPolicyUpdated);
    const isPartMasterUpdated: boolean = useSelector((state: RootState) => state.masterTables.isPartMasterUpdated);
    const isOrderPolicyUpdated: boolean = useSelector((state: RootState) => state.planningParametersTab.isOrderPolicyUpdated);
    const isSafetyStockPolicyUpdated: boolean = useSelector((state: RootState) => state.planningParametersTab.isSafetyStockPolicyUpdated);

    /**
   * Fetches Lot Size Optimization Policy data from API
   * @param {any} body Request body
   */
    const fetchLotSizeOptimizationPolicy = useCallback(async (body: any): Promise<void> => {
        setChangeTrackState(false);
        const fetchingDataToastID = toast.loading("Fetching Lot Size Optimization Policy data...", {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions("loading"),
        });
        try {
            const fetchAPIResponse: AxiosResponse<LotSizeOptimizationPolicyFetchAPI> = await fetchData(`/lot-size-optimization-policy`, {
                method: 'POST',
                data: body,
            });

            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID,
            });

            if (fetchAPIResponse.data.data) {
                setGridRowData(fetchAPIResponse.data.data.result);
                setGridRowDataCopy(
                    JSON.parse(JSON.stringify(fetchAPIResponse.data.data.result))
                );

                // Set dropdown values
                setPartCodes(fetchAPIResponse.data.cell_dropdowns?.part_code as string[]);
                setOrderPolicies(fetchAPIResponse.data.cell_dropdowns?.order_policy as string[])
                setSafetyStockPolicies(fetchAPIResponse.data.cell_dropdowns?.safety_stock_policy as string[]);

                // Set dropdown mappings
                setPartCodeMappings(fetchAPIResponse.data.mappings?.part_code as any);

                dispatchFn(planningParametersTabActions.setTabApiDataFetched({
                    [LOT_SIZE_OPTIMIZATION_POLICY_TAB_ID]: true
                }));

            } else {
                toast.error("Error in fetching Lot Size Optimization Policy data", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("error"),
                });
            }
        } catch (error: Error | any) {
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID,
            });
            toast.error("Error in fetching Lot Size Optimization Policy data", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error"),
            });
        }
    }, [dispatchFn, fetchData]);

    /**
   * Event handler for when cell editing is stopped
   * @param {any} params Cell editing stop event parameters
   */
    const onCellEditingStoppedHandler = (params: any) => {
        try {
            const id: string = params.data.group_policy_id;
            const colName: string = params.column.colId;

            if (id !== undefined && id.toString().startsWith("new_")) {
                setInsertedRows((prev: any) => {
                    let newRow = prev[id] ? prev[id] : {};

                    switch (colName) {
                        case "part_name":
                            newRow["part_code"] = partCodeMappings[params.newValue];
                            break;
                        default:
                            newRow[colName] = params.newValue;
                    }

                    return {
                        ...prev,
                        [id]: newRow
                    };
                });
            } else {
                if (params.oldValue !== params.newValue && params.newValue !== undefined) {
                    let currentRowNode = agGridRef.current!.api!.getRowNode(params.node.id);

                    setCellEditTracks((prev) => {
                        let newTrackEdits: any = prev;
                        let cellEditTrack = newTrackEdits[id] ? newTrackEdits[id] : {};

                        switch (colName) {
                            case "part_name":
                                cellEditTrack["part_code"] = partCodeMappings[params.newValue];
                                break;
                            default:
                                cellEditTrack[colName] = params.newValue;
                        }

                        return {
                            ...prev,
                            [id]: cellEditTrack
                        };
                    });
                    dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'lotSizeOptimizationPolicy', value: true }));

                    setChangeTrackState(true);
                    agGridRef.current!.api!.applyTransaction({
                        update: [currentRowNode?.data],
                    });
                } else if (params.newValue === params.oldValue) {
                    dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'lotSizeOptimizationPolicy', value: false }));

                }
            }

        } catch (error: any) {
            console.error(`Error: ${error}`);
        }
    };

    /**
   * Sets selected rows on selection checkbox change
   */
    const onSelectionChangedHandler = () => {
        const selectedRows = agGridRef.current!.api!.getSelectedRows();
        const selectedRowIds: any[] = selectedRows.map((el) => el.group_policy_id);
        setSelectedRowIds(selectedRowIds);
    };

    /**
   * Deletes selected rows
   * @param {any[]} selectedRowIds Selected Row IDs
   */
    const deleteSelectedRowData = async (selectedRowIds: any[]) => {
        let deleteRowsToastID;

        try {
            // Delete selected newly inserted rows
            const oldDataRowIds: number[] = selectedRowIds.filter(rowId => !rowId.toString().startsWith("new_")).map(rowId => parseInt(rowId));
            setInsertedRows((insertedRows: any) => removeInsertedRowsById(insertedRows, selectedRowIds));

            if (oldDataRowIds.length) {
                deleteRowsToastID = toast.loading("Deleting selected rows...", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("loading"),
                });

                const deleteAPIResponse: AxiosResponse<{
                    data?: string;
                    error?: string;
                }> = await fetchData(`/lot-size-optimization-policy`, {
                    method: 'DELETE',
                    data: {
                        ids: oldDataRowIds
                    },
                });

                toast.dismiss({
                    id: deleteRowsToastID,
                    containerId: TOAST_CONTAINER_ID,
                });

                if (deleteAPIResponse.data.data) {
                    setCellEditTracks({});
                    setInsertedRows({});

                    dispatchFn(planningParametersTabActions.setTabApiDataFetched({
                        [LOT_SIZE_OPTIMIZATION_POLICY_TAB_ID]: false
                    }));

                    toast.success(deleteAPIResponse.data.data, {
                        containerId: TOAST_CONTAINER_ID,
                        ...getToastOptions("success"),
                    });

                } else {
                    toast.error(deleteAPIResponse.data.error, {
                        containerId: TOAST_CONTAINER_ID,
                        ...getToastOptions("error"),
                    });
                }
            } else {
                const selectedRows: any[] | undefined = agGridRef.current?.api.getSelectedRows();
                agGridRef.current?.api.applyTransaction({
                    remove: selectedRows
                });
            }
        } catch (error: any) {
            console.error(error);

            if (deleteRowsToastID) {
                toast.error("Failed to delete the selected rows", {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("error"),
                });
            }
        }
    };

    /**
     * Updates cell values by using part site source update API
     * @param {any} cellEditTracks Cell edits in the grid
     * @param {any} insertedRows Newly inserted rows
     */
    const updateCellValues = async (
        cellEditTracks: any,
        insertedRows: any
    ): Promise<void> => {
        try {
            const updateToastID = toast.loading("Updating Lot Size Optimization Policy data...", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("loading"),
            });

            const updateAPIResponse: AxiosResponse<{
                data?: string;
                error?: string;
            }> = await fetchData(`/lot-size-optimization-policy`, {
                method: 'PUT',
                data: {
                    update_data: cellEditTracks,
                    insert_data: insertedRows
                }
            });

            toast.dismiss({
                id: updateToastID,
                containerId: TOAST_CONTAINER_ID,
            });

            if (updateAPIResponse.data.data) {
                setCellEditTracks({});
                setInsertedRows({});

                dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'lotSizeOptimizationPolicy', value: false }));
                dispatchFn(planningParametersTabActions.setIsLotSizeOptimizationPolicyUpdated(!isLotSizeOptimizationPolicyUpdated));
                dispatchFn(planningParametersTabActions.setTabApiDataFetched({
                    [LOT_SIZE_OPTIMIZATION_POLICY_TAB_ID]: false,
                    [LOT_SIZE_GROUPS_TAB_ID]: false
                }));

                toast.success(updateAPIResponse.data.data, {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("success"),
                });

            } else {
                toast.error(updateAPIResponse.data.error, {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("error"),
                });
            }
        } catch (error: any) {
            console.error(error);
            toast.error("Error in updating the Lot Size Optimization Policy data", {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions("error"),
            });
        }
    };

    /**
     * Event handler for save button click
     * @param {any} event Save button click event data
     */
    const onUpdateCellValuesHandler = async (event: any): Promise<void> => {
        try {
            const updatedNewRowData: any = {};
            for (let key in insertedRows) {
                if (Object.keys(insertedRows[key]).length) {
                    updatedNewRowData[key] = insertedRows[key];
                }
            }

            if (
                Object.keys(cellEditTracks).length ||
                Object.keys(updatedNewRowData).length
            ) {
                updateCellValues(cellEditTracks, updatedNewRowData);

            }
        } catch (error: Error | any) {
            console.error(`Error: ${error}`);
        }
    };

    /**
     * Event handler for reset button click
     * @param {any} params Reset button click event data
     */
    const resetCellEdits = async (event: any): Promise<void> => {
        try {
            const resetConsent: boolean = window.confirm(
                "Do you want to reset the changes done to the grid?"
            );

            if (resetConsent) {
                setCellEditTracks({});
                setChangeTrackState(false);
                setGridRowDataCopy(JSON.parse(JSON.stringify([...gridRowData])));
                dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({
                    key: 'lotSizeOptimizationPolicy',
                    value: false
                }));
            }
        } catch (error: Error | any) {
            console.error(`Error: ${error}`);
        }
    };

    /**
     * Inserts a new row into the grid
     */
    const addRowToGrid = (): void => {
        const newRowId: string = `new_${newRowCount.toString()}`;
        agGridRef.current!.api!.applyTransaction({
            add: [{ group_policy_id: newRowId }],
            addIndex: 0,
        });
        setInsertedRows((insertedRows: any) => {
            return {
                ...insertedRows,
                [newRowId]: {}
            };
        });
        setChangeTrackState(true);
        setNewRowCount((rowCount) => (rowCount + 1));
        dispatchFn(planningParametersTabActions.setIsPlanningParametersTabEdited({ key: 'lotSizeOptimizationPolicy', value: true }));
    }

    /**
     * Deletes selected rows from the grid
     */
    const deleteSelectedRowsFromGrid = (): void => {
        deleteSelectedRowData(selectedRowIds);
        setSelectedRowIds([]);
        setEnableDelButton(false);
    };

    useEffect(() => {
        if (
            !tabApiDataFetched &&
            activeUpperSubTabId === LOT_SIZE_OPTIMIZATION_POLICY_TAB_ID
        ) {
            fetchLotSizeOptimizationPolicy({
                part_codes: selectedPartSlicers
            });
        }
    }, [
        fetchLotSizeOptimizationPolicy,
        tabApiDataFetched,
        activeUpperSubTabId,
        selectedPartSlicers,
        isPartMasterUpdated,
        isOrderPolicyUpdated,
        isSafetyStockPolicyUpdated
    ]);

    useEffect(() => {
        if (selectedRowIds.length > 0) {
            setEnableDelButton(true);
        } else {
            setEnableDelButton(false);
        }
    }, [selectedRowIds]);

    useEffect(() => {
        if (
            Object.keys(insertedRows).length === 0 &&
            Object.keys(cellEditTracks).length === 0
        ) {
            setChangeTrackState(false);
        }
    }, [insertedRows, cellEditTracks]);

    return (
        <>
            <div className="row mb-2">
                <div className="col offset-8 col-4 text-end">
                    <EditableGridActions
                        tabId={LOT_SIZE_OPTIMIZATION_POLICY_TAB_ID}
                        disableIf={{
                            resetBtn: !changeTrackState,
                            saveBtn: !changeTrackState,
                            deleteRowBtn: !enableDelButton
                        }}
                        onClick={{
                            resetBtn: resetCellEdits,
                            saveBtn: onUpdateCellValuesHandler,
                            addRowBtn: addRowToGrid,
                            deleteRowBtn: deleteSelectedRowsFromGrid
                        }}
                    />
                </div>
            </div>

            <div
                className="ag-theme-balham mt-2"
                style={{
                    height: props.gridHeight,
                    maxHeight: props.gridHeight,
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "auto",
                }}
            >
                <AgGridReact
                    ref={agGridRef}
                    rowData={gridRowDataCopy}
                    columnDefs={gridColDef}
                    defaultColDef={{
                        ...(AG_GRID_DEFAULT_COL_DEF as any),
                        width: 200,
                        editable: true,
                    }}
                    rowSelection={"multiple"}
                    onCellEditingStopped={onCellEditingStoppedHandler}
                    gridOptions={{
                        ...(AG_GRID_OPTIONS as any),
                        onSelectionChanged: onSelectionChangedHandler,
                        getRowStyle: (params: any) => newGridRowStyleHandler(params, "group_policy_id")
                    }}
                    modules={AG_GRID_MODULES}
                />
            </div>
        </>
    );
};

export default LotSizeOptimizationPolicyGrid;
